import { useEffect, useMemo, useState, useImperativeHandle, forwardRef, useRef, useCallback } from "react";
import MathRenderer from "./math-renderer";

// Import Yoopta Editor Core
import YooptaEditor, { createYooptaEditor, YooptaContentValue } from '@yoopta/editor';

// Import Yoopta Plugins
import Paragraph from '@yoopta/paragraph';
import Blockquote from '@yoopta/blockquote';
import Image from '@yoopta/image';
import Link from '@yoopta/link';
import { NumberedList, BulletedList, TodoList } from '@yoopta/lists';
import Code from '@yoopta/code';
import { HeadingOne, HeadingTwo, HeadingThree } from '@yoopta/headings';
import Divider from '@yoopta/divider';
import LinkTool, { DefaultLinkToolRender } from '@yoopta/link-tool';
import ActionMenu, { DefaultActionMenuRender } from '@yoopta/action-menu-list';
import Toolbar, { DefaultToolbarRender } from '@yoopta/toolbar';
import { Bold, Italic, CodeMark, Underline, Strike, Highlight } from '@yoopta/marks';
import 'katex/dist/katex.min.css';

// Import Markdown Serialization
import { markdown } from '@yoopta/exports';

const MARKS = [Bold, Italic, CodeMark, Underline, Strike, Highlight];

const TOOLS = {
  Toolbar: {
    tool: Toolbar,
    render: DefaultToolbarRender,
  },
  ActionMenu: {
    tool: ActionMenu,
    render: DefaultActionMenuRender,
  },
  LinkTool: {
    tool: LinkTool,
    render: DefaultLinkToolRender,
  },
};

// Import ShadCN UI Components
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Star, ChevronRight } from "lucide-react";
import { useStartItem, useStopItem, useUpsertWatchTime } from "@/hooks/hooks";
import { useCourseStore } from "@/store/course-store";



// ✅ PLUGINS for Yoopta Editor (read-only display)
const plugins = [
    Paragraph,
    Blockquote,
    Link,
    Image,
    Divider,
    HeadingOne,
    HeadingTwo,
    HeadingThree,
    NumberedList,
    BulletedList,
    TodoList,
    Code,
];
import type { ArticleProps, ArticleRef } from "@/types/article.types";
import { NavigatingOverlay } from "./video";
import { toast } from "sonner";


const Article = forwardRef<ArticleRef, ArticleProps>(({ content, estimatedReadTimeInMinutes, points, tags, onNext, isProgressUpdating, isAlreadyWatched, completedItemIdsRef }, ref) => {
    // ✅ Initialize Yoopta Editor
    const editor = useMemo(() => createYooptaEditor(), []);
    const [value, setValue] = useState<YooptaContentValue>();
    
    // ✅ Get user and course data from stores
    const { currentCourse, setWatchItemId } = useCourseStore();
    const startItem = useStartItem();
    const stopItem = useStopItem();
    const upsertWatchTime = useUpsertWatchTime();
    const [isStopping, setIsStopping] = useState(false);

    // ✅ Track if item has been started and if start request has been sent
    const itemStartedRef = useRef(false);
    const startRequestSentRef = useRef(false);
    const stopInFlightRef = useRef(false);
    // Mirrors video.tsx's watchItemIdRef: currentCourse.watchItemId comes from
    // the store and is only visible to this closure after a re-render, so a
    // stop call made synchronously after start resolves (see startPromiseRef
    // below) could still read a stale, unset value from it. A ref updated in
    // the same tick as itemStartedRef avoids that gap.
    const watchItemIdRef = useRef<string | null>(null);
    // Resolves once the in-flight start request settles (success or failure).
    // handleNextClick awaits this before checking itemStartedRef so a fast
    // click can't race ahead of the start confirmation and silently skip the
    // stop call -- see handleNextClick below for what that race used to do.
    const startPromiseRef = useRef<Promise<void> | null>(null);
    // Distinguishes "never attempted a start" (item already watched, nothing
    // to record) from "attempted and failed" -- itemStartedRef.current is
    // false in both cases, but only the second should block navigation. A
    // request that errors, or resolves with no watchItemId, sets this so
    // handleNextClick can tell the difference after awaiting startPromiseRef.
    const startFailedRef = useRef(false);

    function handleSendStartItem() {
        if (!currentCourse?.itemId || startRequestSentRef.current) return;
        // Mark that we've sent the start request to prevent multiple calls
        startRequestSentRef.current = true;
        startFailedRef.current = false;
        if(!isAlreadyWatched && (currentCourse!.itemId && !completedItemIdsRef.current.has(currentCourse!.itemId))){
            startPromiseRef.current = startItem.mutateAsync({
                params: {
                    path: {
                        courseId: currentCourse.courseId,
                        courseVersionId: currentCourse.versionId ?? '',
                    },
                },
                body: {
                    itemId: currentCourse.itemId,
                    moduleId: currentCourse.moduleId ?? '',
                    sectionId: currentCourse.sectionId ?? '',
                    cohortId: currentCourse.cohortId || undefined,
                }
            }).then((data) => {
                if (data?.watchItemId) {
                    watchItemIdRef.current = data.watchItemId;
                    setWatchItemId(data.watchItemId);
                    itemStartedRef.current = true;
                } else {
                    // No error thrown, but nothing to track either -- treat
                    // the same as a failure rather than silently proceeding.
                    startFailedRef.current = true;
                }
            }).catch((error) => {
                console.error('❌ handleSendStartItem error:', error);
                startFailedRef.current = true;
            });
        }
    }

   async function handleStopItem() {
        const watchItemId = watchItemIdRef.current || currentCourse?.watchItemId;
        if (!currentCourse?.itemId || !watchItemId || !itemStartedRef.current) return;
        if (stopInFlightRef.current) return;

        stopInFlightRef.current = true;
        try {
            if(!isAlreadyWatched && (currentCourse!.itemId && !completedItemIdsRef.current.has(currentCourse!.itemId))){
                await stopItem.mutateAsync({
                    params: {
                        path: {
                            courseId: currentCourse.courseId,
                            courseVersionId: currentCourse.versionId ?? '',
                        },
                    },
                    body: {
                        watchItemId,
                        itemId: currentCourse.itemId,
                        moduleId: currentCourse.moduleId ?? '',
                        sectionId: currentCourse.sectionId ?? '',
                        cohortId: currentCourse.cohortId || undefined,
                    }
                });
                completedItemIdsRef.current.add(currentCourse!.itemId);
            }

            itemStartedRef.current = false;
        } catch (error: any) {
            console.error('❌ handleStopItem error:', error);
            throw error;
        } finally {
            stopInFlightRef.current = false;
        }
    }

    /**
     * The Yoopta link plugin defaults new links to target="_self" (its own
     * `props: { target: "_self" }` default) unless a content author manually
     * overrode it per link, so resource links routinely open in the same tab
     * rather than the new tab this flow is designed around. Force it here
     * instead of depending on every author remembering to set target="_blank"
     * on every link. Plain left-clicks only — modifier-key/middle clicks are
     * left to the browser's own new-tab handling.
     */
    const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const anchor = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
        if (!anchor) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        window.open(anchor.href, '_blank', 'noopener,noreferrer');
    };

    // // ✅ Handle Next button click - send stop request only when user clicks Next
    // const handleNextClick = () => {
    //     if (itemStartedRef.current) {
    //         handleStopItem();
    //     }
    //     if (onNext) {
    //         onNext();
    //     }
    // };

    const handleNextClick = async () => {
        if (isStopping || isProgressUpdating) return;

        try {
            setIsStopping(true);
            // A click landing before the start request's response arrives used
            // to read itemStartedRef.current as "never started" and skip the
            // stop call entirely -- the item then never got marked complete,
            // permanently locking the next one. Wait for the in-flight start to
            // settle first so itemStartedRef reflects reality before deciding.
            if (startRequestSentRef.current && !itemStartedRef.current && startPromiseRef.current) {
                await startPromiseRef.current;
            }
            // A failed (or empty-response) start left itemStartedRef false with
            // nothing to stop -- previously that looked identical to "already
            // watched, nothing to record" and navigated on anyway, silently
            // losing the attempt. Retry once before giving up: handleSendStartItem
            // guards on startRequestSentRef, so without clearing it here a failed
            // attempt could never be retried by clicking Next again -- the error
            // below would tell the student to "try again" with no way to.
            if (startFailedRef.current) {
                startRequestSentRef.current = false;
                handleSendStartItem();
                if (startPromiseRef.current) {
                    await startPromiseRef.current;
                }
            }
            if (startFailedRef.current) {
                throw new Error('We could not save your progress. Please try again.');
            }
            if (itemStartedRef.current) {
            await handleStopItem(); //  wait until stop finishes
            }

            onNext?.(); //  only after stop succeeds
        } catch (err: any) {
            // toast.error('Unable to save progress. Please try again.');
            toast.warning(err.response?.data?.message || err.message || 'You must spend more time reading this article to proceed.');
            console.error('Stop item failed:', err);
        } finally {
            setIsStopping(false);
        }
    };

    // ✅ Expose stop function to parent component
    useImperativeHandle(ref, () => ({
        stopItem: handleStopItem
    }));

    // Keep a ref to the latest stop fn so the unmount cleanup can call it
    // without closing over a stale `currentCourse`.
    const handleStopItemRef = useRef(handleStopItem);
    handleStopItemRef.current = handleStopItem;

    // ✅ Call upsert watch time API every 10 seconds while article is being read
    useEffect(() => {
        if (!currentCourse?.watchItemId || !itemStartedRef.current) return;

        const interval = setInterval(() => {
            upsertWatchTime.mutate({
                body: {
                    watchItemId: currentCourse.watchItemId!,
                    itemId: currentCourse.itemId!,
                    cohortId: currentCourse.cohortId || undefined,
                }
            } as any);
        }, 15000); // every 15 seconds

        return () => clearInterval(interval); // cleanup on unmount
    }, [currentCourse?.watchItemId]);

    // ✅ Load content from prop when component mounts or content changes
    useEffect(() => {
        if (content) {
            // Preprocess content to handle math expressions better
            let processedContent = content;
            
            // Ensure math expressions are properly formatted
            // Convert \( \) to $ $ for inline math
            processedContent = processedContent.replace(/\\\((.*?)\\\)/gs, '$$$1$$');
            // Convert \[ \] to $$ $$ for display math
            processedContent = processedContent.replace(/\\\[(.*?)\\\]/gs, '$$$$1$$$$');
            
            // Fix common LaTeX formatting issues
            // Ensure proper escaping for backslashes in math contexts
            processedContent = processedContent.replace(/\$\$(.*?)\$\$/gs, (_, mathContent) => {
                // Clean up the math content - remove extra escaping that might interfere
                const cleanMath = mathContent.replace(/\\n/g, ' ').replace(/\s+/g, ' ').trim();
                return `$$${cleanMath}$$`;
            });
            
            const deserializedValue = markdown.deserialize(editor, processedContent);
            setValue(deserializedValue);
            editor.setEditorValue(deserializedValue);

            // ✅ Start tracking item when content is loaded (only once)
            handleSendStartItem();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, content]);

    // ✅ Clean up on unmount
    useEffect(() => {
        return () => {
            // Defense in depth: if this document was opened but is being left by a
            // path that didn't explicitly stop it (e.g. browser back / tab change),
            // record its completion so it still gets ticked. Skipped if a stop is
            // already in flight (e.g. user clicked Next — handleNextClick awaited
            // handleStopItem but the component unmounted before the Promise resolved).
            if (itemStartedRef.current && !stopInFlightRef.current) {
                void handleStopItemRef.current?.();
            }
            itemStartedRef.current = false;
            startRequestSentRef.current = false;
            watchItemIdRef.current = null;
        };
    }, []);

    // Snapshot for the pagehide flush below, refreshed every render so the
    // handler (registered once) always reads current values.
    const pageHideContextRef = useRef({ currentCourse, isAlreadyWatched });
    pageHideContextRef.current = { currentCourse, isAlreadyWatched };

    /**
     * Mirrors video.tsx's pagehide handler: a resource link inside an article
     * that navigates the same tab (many links default to target="_self", and
     * some browsers/webviews override target="_blank" anyway) tears down this
     * component before the unmount effect's stop request can complete, since
     * a non-keepalive request gets cancelled by the navigation. Without this,
     * the item is left "started" forever with no watchTime record, which is
     * what locked students out of subsequent sections in production.
     *
     * Raw `fetch` with keepalive (not navigator.sendBeacon, which can't set
     * the Authorization header this API requires) so the request survives the
     * page unload. Fire-and-forget by design — the page may already be gone
     * by the time this returns.
     */
    const handlePageHide = useCallback(() => {
        const { currentCourse, isAlreadyWatched } = pageHideContextRef.current;
        const watchItemId = watchItemIdRef.current || currentCourse?.watchItemId;
        if (
            !itemStartedRef.current ||
            stopInFlightRef.current ||
            isAlreadyWatched ||
            !currentCourse?.itemId ||
            !watchItemId ||
            completedItemIdsRef.current.has(currentCourse.itemId)
        ) {
            return;
        }

        const token = localStorage.getItem('firebase-auth-token');
        if (!token) return;

        const url =
            `${import.meta.env.VITE_BASE_URL}/users/progress/courses/${currentCourse.courseId}` +
            `/versions/${currentCourse.versionId ?? ''}/stop`;

        try {
            void fetch(url, {
                method: 'POST',
                keepalive: true,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    watchItemId,
                    itemId: currentCourse.itemId,
                    moduleId: currentCourse.moduleId ?? '',
                    sectionId: currentCourse.sectionId ?? '',
                    cohortId: currentCourse.cohortId || undefined,
                }),
            });
        } catch {
            // Best-effort — the tab is already closing, nothing left to do if this throws.
        }
    }, [completedItemIdsRef]);

    useEffect(() => {
        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, [handlePageHide]);

    // ✅ Add dark mode styles for Yoopta Editor
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .dark .yoopta-mark-code {
                background-color: hsl(var(--muted)) !important;
                color: hsl(var(--muted-foreground)) !important;
            }

            .dark .yoopta-link-preview {
                background-color: hsl(var(--popover)) !important;
                border-color: hsl(var(--border)) !important;
                color: hsl(var(--popover-foreground)) !important;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);


    return (
        <MathRenderer className="h-full w-full bg-background">
            <div className="h-full w-full flex flex-col">
                <NavigatingOverlay
                    visible={isStopping}
                    title="Verifying answers"
                    message="Please wait while we submit and validate your responses…"
                />
                {/* Article Metadata Topbar */}
                {(estimatedReadTimeInMinutes || points || tags?.length) && (
                    <div className="border-b bg-muted/50 px-4 py-3">
                        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            {estimatedReadTimeInMinutes && (
                                <div className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    <span>{estimatedReadTimeInMinutes} min read</span>
                                </div>
                            )}
                            {points && (
                                <div className="flex items-center gap-1">
                                    <Star className="h-4 w-4" />
                                    <span>{points} points</span>
                                </div>
                            )}
                            {tags && tags.length > 0 && (
                                <div className="flex items-center gap-2">
                                    <span>Tags:</span>
                                    <div className="flex flex-wrap gap-1">
                                        {tags.map((tag, index) => (
                                            <Badge key={index} variant="secondary" className="text-xs">
                                                {tag}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Article Content */}
                <div className="flex-1 w-full p-4 overflow-y-auto" onClick={handleContentClick}>
                    <YooptaEditor
                        width="100%"
                        value={value}
                        editor={editor}
                        plugins={plugins}
                        marks={MARKS}
                        tools={TOOLS}
                        readOnly={true}
                        autoFocus={false}
                        className="prose prose-lg max-w-none w-full dark:prose-invert"
                    />
                </div>

                {/* Next Lesson Button */}
                {onNext && (
                    <div className="p-4 border-t border-border/20 bg-background/50 backdrop-blur-sm">
                        <div className="flex justify-end">
                            <Button
                                onClick={handleNextClick}
                                disabled={isProgressUpdating || isStopping}
                                className="shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground border-0"
                                size="lg"
                            >
                                {isProgressUpdating || isStopping ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground/30 border-t-primary-foreground mr-2" />
                                        Processing
                                    </>
                                ) : (
                                    <>
                                        Next Lesson
                                        <ChevronRight className="h-4 w-4 ml-2" />
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </MathRenderer>
    );
})

Article.displayName = "Article";

export default Article;